import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useData } from '../contexts/DataContext';
import { db, COLLECTIONS } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  orderBy,
  limit,
  deleteDoc,  // הוסף את זה
  doc         // הוסף את זה
} from 'firebase/firestore';
import { 
  Users, 
  Calendar, 
  Eye, 
  Sun, 
  Moon, 
  CheckCircle, 
  XCircle, 
  Target,
  Lightbulb,
  TrendingUp,
  Filter,
  Search,
  ChevronDown,
  ChevronUp,
  CalendarDays,
  CalendarRange,
  CalendarClock,
  BarChart,
  Award,
  Star,
 RefreshCw,  // הוסף את זה
  Trash2      // הוסף את זה
} from 'lucide-react';

const TeamSummaries = () => {
  const { currentUser } = useAuth();
  const { users, departments, teams } = useData();
  
  const [loading, setLoading] = useState(true);
  const [summaries, setSummaries] = useState([]);
  const [openings, setOpenings] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [selectedUser, setSelectedUser] = useState('');
  const [expandedSummary, setExpandedSummary] = useState(null);
  const [viewMode, setViewMode] = useState('summaries'); // 'summaries' or 'openings'
  const [periodType, setPeriodType] = useState('day'); // day, week, month, quarter, year
  const [selectedWeek, setSelectedWeek] = useState(getCurrentWeek());
  const [selectedMonth, setSelectedMonth] = useState(getCurrentMonth());
  const [selectedQuarter, setSelectedQuarter] = useState(getCurrentQuarter());
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());

  // פונקציות עזר לתאריכים
  function getCurrentWeek() {
    const now = new Date();
    const firstDay = new Date(now.setDate(now.getDate() - now.getDay()));
    return firstDay.toISOString().split('T')[0];
  }

  function getCurrentMonth() {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  }

  function getCurrentQuarter() {
    const now = new Date();
    const quarter = Math.floor(now.getMonth() / 3) + 1;
    return `${now.getFullYear()}-Q${quarter}`;
  }

  // קבלת המשתמשים הרלוונטיים לפי תפקיד
  const getRelevantUsers = () => {
    if (!currentUser) return [];
    
    let relevantUsers = [];
    
    switch (currentUser.role) {
      case 'team_manager':
        relevantUsers = users.filter(user => user.teamId === currentUser.teamId);
        break;
        
      case 'dept_manager':
        const departmentTeams = teams.filter(team => team.departmentId === currentUser.departmentId);
        const teamIds = departmentTeams.map(team => team.id);
        relevantUsers = users.filter(user => teamIds.includes(user.teamId));
        break;
        
      case 'ceo':
      case 'deputy_ceo':
      case 'org_admin':
        relevantUsers = users.filter(user => user.role !== 'org_admin');
        break;
        
      default:
        relevantUsers = users.filter(user => user.id === currentUser.id || user.uid === currentUser.uid);
        break;
    }
    
    const currentUserInList = relevantUsers.find(user => 
      user.id === currentUser.id || user.uid === currentUser.uid
    );
    
    if (!currentUserInList) {
      relevantUsers.push(currentUser);
    }
    
    return relevantUsers;
  };

  const relevantUsers = getRelevantUsers();

  useEffect(() => {
    loadData();
  }, [currentUser, selectedDate, selectedUser, viewMode, periodType, selectedWeek, selectedMonth, selectedQuarter, selectedYear]);

  const loadData = async () => {
    if (!currentUser) return;
    
    setLoading(true);
    try {
      let userIds = [];
      
      const currentUserId = currentUser.id || currentUser.uid;
      
      if (selectedUser === 'me') {
        userIds = currentUserId ? [currentUserId] : [];
      } else if (selectedUser) {
        userIds = [selectedUser];
      } else {
        userIds = [...new Set([
          ...relevantUsers.map(user => user.id).filter(id => id),
          ...relevantUsers.map(user => user.uid).filter(uid => uid)
        ])];
        
        if (currentUserId && !userIds.includes(currentUserId)) {
          userIds.push(currentUserId);
        }
      }
      
      // טעינת נתונים לפי סוג תצוגה
      if (viewMode === 'summaries') {
        await loadSummaries(userIds);
      } else {
        await loadOpenings(userIds);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadSummaries = async (userIds) => {
    let allSummaries = [];
    
    // שאילתה לאוספים הרלוונטיים
    const collectionName = periodType === 'day' 
      ? COLLECTIONS.DAY_SUMMARIES 
      : (COLLECTIONS.PERIOD_SUMMARIES || COLLECTIONS.DAY_SUMMARIES);
    
    if (userIds.length > 10 && !selectedUser) {
      for (let i = 0; i < userIds.length; i += 10) {
        const batch = userIds.slice(i, i + 10);
        const summariesQuery = query(
          collection(db, collectionName),
          where('userId', 'in', batch)
        );
        
        const summariesSnapshot = await getDocs(summariesQuery);
        const batchData = summariesSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        allSummaries = [...allSummaries, ...batchData];
      }
    } else {
      const summariesQuery = query(
        collection(db, collectionName),
        where('userId', 'in', userIds)
      );
      
      const summariesSnapshot = await getDocs(summariesQuery);
      allSummaries = summariesSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
    }
    
    // סינון לפי סוג תקופה
    const filteredByType = allSummaries.filter(doc => 
      (doc.periodType === periodType) || (!doc.periodType && periodType === 'day')
    );
    
    // סינון לפי תאריך/תקופה
    const filteredSummaries = filteredByType.filter(doc => {
      return isInSelectedPeriod(doc.date, periodType);
    });
    
    // מיון לפי תאריך
    filteredSummaries.sort((a, b) => {
      const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
      const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
      return dateB - dateA;
    });
    
    setSummaries(filteredSummaries);
  };

  const loadOpenings = async (userIds) => {
    let allOpenings = [];
    
    // טעינת פתיחות יום מה-COLLECTION הישן
    if (periodType === 'day') {
      if (userIds.length > 10 && !selectedUser) {
        for (let i = 0; i < userIds.length; i += 10) {
          const batch = userIds.slice(i, i + 10);
          const openingsQuery = query(
            collection(db, COLLECTIONS.DAY_OPENINGS),
            where('userId', 'in', batch)
          );
          
          const openingsSnapshot = await getDocs(openingsQuery);
          const batchData = openingsSnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          
          allOpenings = [...allOpenings, ...batchData];
        }
      } else {
        const openingsQuery = query(
          collection(db, COLLECTIONS.DAY_OPENINGS),
          where('userId', 'in', userIds)
        );
        
        const openingsSnapshot = await getDocs(openingsQuery);
        allOpenings = openingsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
      }
    }
    
    // טעינת פתיחות תקופות אחרות
    const collectionName = COLLECTIONS.PERIOD_OPENINGS || COLLECTIONS.DAY_OPENINGS;
    
    if (userIds.length > 10 && !selectedUser) {
      for (let i = 0; i < userIds.length; i += 10) {
        const batch = userIds.slice(i, i + 10);
        const openingsQuery = query(
          collection(db, collectionName),
          where('userId', 'in', batch)
        );
        
        const openingsSnapshot = await getDocs(openingsQuery);
        const batchData = openingsSnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        
        allOpenings = [...allOpenings, ...batchData];
      }
    } else {
      const openingsQuery = query(
        collection(db, collectionName),
        where('userId', 'in', userIds)
      );
      
      const openingsSnapshot = await getDocs(openingsQuery);
      const periodOpenings = openingsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));
      
      allOpenings = [...allOpenings, ...periodOpenings];
    }
    
    // סינון לפי סוג תקופה
    const filteredByType = allOpenings.filter(doc => 
      (doc.periodType === periodType) || (!doc.periodType && periodType === 'day')
    );
    
    // סינון לפי תאריך/תקופה
    const filteredOpenings = filteredByType.filter(doc => {
      return isInSelectedPeriod(doc.date, periodType);
    });
    
    // מיון לפי תאריך
    filteredOpenings.sort((a, b) => {
      const dateA = a.date?.toDate ? a.date.toDate() : new Date(a.date);
      const dateB = b.date?.toDate ? b.date.toDate() : new Date(b.date);
      return dateB - dateA;
    });
    
    setOpenings(filteredOpenings);
  };
  // הוסף את הפונקציה החדשה כאן:
  const deleteSummary = async (summaryId, collectionName) => {
    if (!window.confirm('האם אתה בטוח שברצונך למחוק סיכום זה?')) return;
    
    try {
      await deleteDoc(doc(db, collectionName, summaryId));
      await loadData();
      alert('הסיכום נמחק בהצלחה');
    } catch (error) {
      console.error('Error deleting summary:', error);
      alert('אירעה שגיאה במחיקת הסיכום');
    }
  };
  // פונקציה לבדיקה אם תאריך נמצא בתקופה הנבחרת
  const isInSelectedPeriod = (date, type) => {
    let docDate = null;
    
    if (date) {
      if (date.toDate) {
        docDate = date.toDate();
      } else if (date instanceof Date) {
        docDate = date;
      } else if (typeof date === 'string') {
        docDate = new Date(date);
      }
    }
    
    if (!docDate) return false;
    
    switch(type) {
      case 'day':
        const selectedDateString = new Date(selectedDate).toDateString();
        return docDate.toDateString() === selectedDateString;
        
      case 'week':
        const weekStart = new Date(selectedWeek);
        const weekEnd = new Date(weekStart);
        weekEnd.setDate(weekEnd.getDate() + 6);
        return docDate >= weekStart && docDate <= weekEnd;
        
      case 'month':
        const [year, month] = selectedMonth.split('-');
        return docDate.getFullYear() === parseInt(year) && 
               docDate.getMonth() === parseInt(month) - 1;
        
      case 'quarter':
        const [qYear, qNum] = selectedQuarter.split('-Q');
        const quarter = Math.floor(docDate.getMonth() / 3) + 1;
        return docDate.getFullYear() === parseInt(qYear) && 
               quarter === parseInt(qNum);
        
      case 'year':
        return docDate.getFullYear() === selectedYear;
        
      default:
        return false;
    }
  };

  const getUserName = (userId) => {
    const user = users.find(u => u.id === userId || u.uid === userId);
    return user ? user.name : 'לא ידוע';
  };

  const getDepartmentName = (departmentId) => {
    const dept = departments.find(d => d.id === departmentId);
    return dept ? dept.name : 'ללא מחלקה';
  };

  const getTeamName = (teamId) => {
    const team = teams.find(t => t.id === teamId);
    return team ? team.name : 'ללא צוות';
  };

  const formatDate = (date) => {
    const dateObj = date?.toDate ? date.toDate() : new Date(date);
    return dateObj.toLocaleDateString('he-IL', {
      weekday: 'short',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getRatingColor = (rating) => {
    if (rating >= 4) return 'text-green-600 bg-green-100';
    if (rating >= 3) return 'text-yellow-600 bg-yellow-100';
    return 'text-red-600 bg-red-100';
  };

  const getRatingEmoji = (rating) => {
    if (rating >= 4) return '😊';
    if (rating >= 3) return '😐';
    return '😞';
  };

  const getPeriodTitle = (type) => {
    switch(type) {
      case 'day': return 'יום';
      case 'week': return 'שבוע';
      case 'month': return 'חודש';
      case 'quarter': return 'רבעון';
      case 'year': return 'שנה';
      default: return 'תקופה';
    }
  };

  const getPeriodIcon = (type) => {
    switch(type) {
      case 'day': 
        return viewMode === 'summaries' ? <Moon className="w-6 h-6 text-white" /> : <Sun className="w-6 h-6 text-white" />;
      case 'week': 
        return <CalendarDays className="w-6 h-6 text-white" />;
      case 'month': 
        return <Calendar className="w-6 h-6 text-white" />;
      case 'quarter': 
        return <CalendarRange className="w-6 h-6 text-white" />;
      case 'year': 
        return <CalendarClock className="w-6 h-6 text-white" />;
      default: 
        return <Calendar className="w-6 h-6 text-white" />;
    }
  };

  return (
    <div className="p-6">
      {/* Header */}
      <div className="mb-8">
       <div className="flex items-center gap-4 mb-6">
    <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
      <Users className="w-8 h-8 text-white" />
    </div>
    <div className="flex-1">
      <h1 className="text-3xl font-bold text-gray-900">סיכומי הצוות</h1>
      <p className="text-gray-600">צפה בפתיחות וסיכומי תקופות של חברי הצוות</p>
    </div>
    {/* כפתור רענון חדש */}
    <button
      onClick={() => loadData()}
      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-gray-700 font-medium flex items-center gap-2 transition-colors"
    >
      <RefreshCw className="w-4 h-4" />
      רענן נתונים
    </button>
  </div>

        {/* Filters */}
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          {/* Period Type Selector */}
          <div className="mb-4">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              סוג תקופה:
            </label>
            <div className="flex gap-2 flex-wrap">
              {['day', 'week', 'month', 'quarter', 'year'].map(type => (
                <button
                  key={type}
                  onClick={() => setPeriodType(type)}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    periodType === type
                      ? 'bg-blue-600 text-white'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  {getPeriodTitle(type)}
                </button>
              ))}
            </div>
          </div>
          
          {/* Quick Actions */}
          <div className="mb-4 flex gap-2">
            <button
              onClick={() => setSelectedUser('me')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedUser === 'me' 
                  ? 'bg-blue-600 text-white' 
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              הסיכומים שלי
            </button>
            <button
              onClick={() => setSelectedUser('')}
              className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                selectedUser === '' 
                  ? 'bg-green-600 text-white' 
                  : 'bg-green-100 text-green-700 hover:bg-green-200'
              }`}
            >
              כל הצוות
            </button>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            
            {/* View Mode */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                סוג תצוגה
              </label>
              <select
                value={viewMode}
                onChange={(e) => setViewMode(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="summaries">סיכומי {getPeriodTitle(periodType)}</option>
                <option value="openings">פתיחות {getPeriodTitle(periodType)}</option>
              </select>
            </div>
            
            {/* Date/Period Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {periodType === 'day' && 'תאריך'}
                {periodType === 'week' && 'שבוע'}
                {periodType === 'month' && 'חודש'}
                {periodType === 'quarter' && 'רבעון'}
                {periodType === 'year' && 'שנה'}
              </label>
              
              {periodType === 'day' && (
                <input
                  type="date"
                  value={selectedDate}
                  onChange={(e) => setSelectedDate(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              )}
              
              {periodType === 'week' && (
                <input
                  type="date"
                  value={selectedWeek}
                  onChange={(e) => setSelectedWeek(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              )}
              
              {periodType === 'month' && (
                <input
                  type="month"
                  value={selectedMonth}
                  onChange={(e) => setSelectedMonth(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              )}
              
              {periodType === 'quarter' && (
                <select
                  value={selectedQuarter}
                  onChange={(e) => setSelectedQuarter(e.target.value)}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {[1, 2, 3, 4].map(q => (
                    <option key={q} value={`${selectedYear}-Q${q}`}>
                      רבעון {q} - {selectedYear}
                    </option>
                  ))}
                </select>
              )}
              
              {periodType === 'year' && (
                <select
                  value={selectedYear}
                  onChange={(e) => setSelectedYear(parseInt(e.target.value))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  {[2023, 2024, 2025, 2026].map(year => (
                    <option key={year} value={year}>
                      {year}
                    </option>
                  ))}
                </select>
              )}
            </div>
            
            {/* User Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                עובד ספציפי
              </label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">כל העובדים ({relevantUsers.length})</option>
                <option value="me">הסיכומים שלי</option>
                {relevantUsers.map(user => (
                  <option key={user.id} value={user.uid || user.id}>
                    {user.name} - {user.role}
                  </option>
                ))}
              </select>
            </div>
            
            {/* Stats */}
            <div className="flex items-end">
              <div className="bg-blue-50 rounded-lg p-3 w-full">
                <div className="text-sm text-blue-600 font-medium">
                  {viewMode === 'summaries' ? 'סיכומים' : 'פתיחות'} {getPeriodTitle(periodType)}
                  {selectedUser === 'me' && ' (שלי)'}
                </div>
                <div className="text-2xl font-bold text-blue-700">
                  {viewMode === 'summaries' ? summaries.length : openings.length}
                </div>
                {selectedUser === 'me' && (
                  <div className="text-xs text-blue-500 mt-1">
                    מתוך {relevantUsers.length} עובדים בצוות
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      ) : (
        <div className="space-y-6">
          
          {/* Summaries */}
          {viewMode === 'summaries' && (
            <div className="space-y-4">
              {summaries.length === 0 ? (
                <div className="text-center py-12">
                  <Moon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    אין סיכומי {getPeriodTitle(periodType)} לתקופה זו
                  </h3>
                  <p className="text-gray-500">
                    נסה לבחור תקופה אחרת או עובד ספציפי
                  </p>
                </div>
              ) : (
                summaries.map(summary => (
                  <div key={summary.id} className="bg-white rounded-xl shadow-sm border border-gray-200">
                    
                    {/* Header */}
                    <div 
                      className="p-6 cursor-pointer hover:bg-gray-50"
                      onClick={() => setExpandedSummary(expandedSummary === summary.id ? null : summary.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-purple-500 rounded-full flex items-center justify-center">
                            {getPeriodIcon(summary.periodType || 'day')}
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {getUserName(summary.userId)}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>{getDepartmentName(summary.departmentId)}</span>
                              {summary.teamId && <span>• {getTeamName(summary.teamId)}</span>}
                              <span>• {formatDate(summary.date)}</span>
                              {summary.periodType && summary.periodType !== 'day' && (
                                <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-xs font-medium">
                                  {getPeriodTitle(summary.periodType)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          <div className={`px-3 py-1 rounded-full text-sm font-medium ${getRatingColor(summary.overallRating)}`}>
                            {getRatingEmoji(summary.overallRating)} {summary.overallRating}/5
                          </div>
                          {expandedSummary === summary.id ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {expandedSummary === summary.id && (
                      <div className="px-6 pb-6 border-t border-gray-100">
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mt-6">
                          
                          {/* הישגים */}
                          {summary.achievements && (
                            <div>
                              <h4 className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                                <CheckCircle className="w-5 h-5 text-green-600" />
                                הישגי ה{getPeriodTitle(summary.periodType || 'day')}
                              </h4>
                              <p className="text-gray-700 bg-green-50 p-3 rounded-lg">
                                {summary.achievements}
                              </p>
                            </div>
                          )}
                          
                          {/* אתגרים */}
                          {summary.challenges && (
                            <div>
                              <h4 className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                                <XCircle className="w-5 h-5 text-red-600" />
                                אתגרים וחסמים
                              </h4>
                              <p className="text-gray-700 bg-red-50 p-3 rounded-lg">
                                {summary.challenges}
                              </p>
                            </div>
                          )}
                          
                          {/* תובנות */}
                          {summary.insights && (
                            <div>
                              <h4 className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                                <Lightbulb className="w-5 h-5 text-yellow-600" />
                                תובנות
                              </h4>
                              <p className="text-gray-700 bg-yellow-50 p-3 rounded-lg">
                                {summary.insights}
                              </p>
                            </div>
                          )}
                          
                          {/* תכנון לתקופה הבאה */}
                          {(summary.nextPeriodPlan || summary.tomorrowTasks) && (
                            <div>
                              <h4 className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                                <Target className="w-5 h-5 text-blue-600" />
                                תכנון ל{getPeriodTitle(summary.periodType || 'day')} הבא
                              </h4>
                              <p className="text-gray-700 bg-blue-50 p-3 rounded-lg">
                                {summary.nextPeriodPlan || summary.tomorrowTasks}
                              </p>
                            </div>
                          )}
                          
                          {/* לקחים שנלמדו - למחזורים ארוכים */}
                          {summary.periodType && summary.periodType !== 'day' && summary.lessonsDetails && (
                            <div>
                              <h4 className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                                <TrendingUp className="w-5 h-5 text-purple-600" />
                                לקחים שנלמדו
                              </h4>
                              <p className="text-gray-700 bg-purple-50 p-3 rounded-lg">
                                {summary.lessonsDetails}
                              </p>
                            </div>
                          )}
                        </div>
                        
                        {/* סטטיסטיקות */}
                        <div className="mt-6 pt-6 border-t border-gray-100">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            {summary.periodType === 'day' || !summary.periodType ? (
                              <>
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-green-600">
                                    {summary.completedTasks?.length || 0}
                                  </div>
                                  <div className="text-sm text-gray-500">משימות הושלמו</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-red-600">
                                    {summary.incompleteTasks?.length || 0}
                                  </div>
                                  <div className="text-sm text-gray-500">משימות לא הושלמו</div>
                                </div>
                              </>
                            ) : (
                              <>
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-green-600">
                                    {summary.goalsAchieved || 0}
                                  </div>
                                  <div className="text-sm text-gray-500">יעדים הושגו</div>
                                </div>
                                <div className="text-center">
                                  <div className="text-2xl font-bold text-blue-600">
                                    {summary.progressPercentage || 0}%
                                  </div>
                                  <div className="text-sm text-gray-500">התקדמות</div>
                                </div>
                              </>
                            )}
                            <div className="text-center">
                              <div className={`text-2xl font-bold ${getRatingColor(summary.overallRating).split(' ')[0]}`}>
                                {summary.overallRating}/5
                              </div>
                              <div className="text-sm text-gray-500">דירוג כללי</div>
                            </div>
                          </div>
                      
                         </div>
                        {/* כפתור מחיקה */}
                        {(currentUser.role === 'org_admin' || currentUser.role === 'ceo') && (
                          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteSummary(
                                  summary.id, 
                                  summary.periodType === 'day' 
                                    ? COLLECTIONS.DAY_SUMMARIES 
                                    : COLLECTIONS.PERIOD_SUMMARIES
                                );
                              }}
                              className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              מחק סיכום
                            </button>
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Openings */}
          {viewMode === 'openings' && (
            <div className="space-y-4">
              {openings.length === 0 ? (
                <div className="text-center py-12">
                  <Sun className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                  <h3 className="text-xl font-semibold text-gray-600 mb-2">
                    אין פתיחות {getPeriodTitle(periodType)} לתקופה זו
                  </h3>
                  <p className="text-gray-500">
                    נסה לבחור תקופה אחרת או עובד ספציפי
                  </p>
                </div>
              ) : (
                openings.map(opening => (
                  <div key={opening.id} className="bg-white rounded-xl shadow-sm border border-gray-200">
                    
                    {/* Header */}
                    <div 
                      className="p-6 cursor-pointer hover:bg-gray-50"
                      onClick={() => setExpandedSummary(expandedSummary === opening.id ? null : opening.id)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-4">
                          <div className="w-12 h-12 bg-gradient-to-r from-orange-500 to-yellow-500 rounded-full flex items-center justify-center">
                            {getPeriodIcon(opening.periodType || 'day')}
                          </div>
                          <div>
                            <h3 className="text-lg font-semibold text-gray-900">
                              {getUserName(opening.userId)}
                            </h3>
                            <div className="flex items-center gap-4 text-sm text-gray-500">
                              <span>{getDepartmentName(opening.departmentId)}</span>
                              {opening.teamId && <span>• {getTeamName(opening.teamId)}</span>}
                              <span>• {formatDate(opening.date)}</span>
                              {opening.periodType && opening.periodType !== 'day' && (
                                <span className="px-2 py-1 bg-orange-100 text-orange-700 rounded text-xs font-medium">
                                  {getPeriodTitle(opening.periodType)}
                                </span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        <div className="flex items-center gap-3">
                          {opening.moodEnergy && (
                            <div className={`px-3 py-1 rounded-full text-sm font-medium ${getRatingColor(opening.moodEnergy)}`}>
                              {getRatingEmoji(opening.moodEnergy)} {opening.moodEnergy}/5
                            </div>
                          )}
                          {expandedSummary === opening.id ? (
                            <ChevronUp className="w-5 h-5 text-gray-400" />
                          ) : (
                            <ChevronDown className="w-5 h-5 text-gray-400" />
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Expanded Content */}
                    {expandedSummary === opening.id && (
                      <div className="px-6 pb-6 border-t border-gray-100">
                        <div className="space-y-6 mt-6">
                          
                          {/* סקירת התקופה */}
                          {(opening.overview || opening.dailyOverview) && (
                            <div>
                              <h4 className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                                <Calendar className="w-5 h-5 text-blue-600" />
                                סקירת ה{getPeriodTitle(opening.periodType || 'day')}
                              </h4>
                              <p className="text-gray-700 bg-blue-50 p-3 rounded-lg">
                                {opening.overview || opening.dailyOverview}
                              </p>
                            </div>
                          )}
                          
                          {/* יעדים - למחזורים ארוכים */}
                          {opening.goals && opening.goals.length > 0 && (
                            <div>
                              <h4 className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                                <Target className="w-5 h-5 text-green-600" />
                                יעדי ה{getPeriodTitle(opening.periodType || 'day')}
                              </h4>
                              <ul className="space-y-2">
                                {opening.goals.map((goal, index) => (
                                  <li key={index} className="flex items-start gap-2">
                                    <div className="w-6 h-6 bg-green-500 text-white rounded-full flex items-center justify-center text-xs font-bold mt-0.5">
                                      {index + 1}
                                    </div>
                                    <span className="text-gray-700">{goal}</span>
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* פרויקטים מרכזיים - למחזורים ארוכים */}
                          {opening.projects && opening.projects.length > 0 && (
                            <div>
                              <h4 className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                                <BarChart className="w-5 h-5 text-indigo-600" />
                                פרויקטים מרכזיים
                              </h4>
                              <ul className="space-y-2">
                                {opening.projects.map((project, index) => (
                                  <li key={index} className="p-2 bg-indigo-50 rounded-lg text-gray-700">
                                    {project}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* KPIs - לרבעון ושנה */}
                          {opening.kpis && opening.kpis.length > 0 && (
                            <div>
                              <h4 className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                                <Star className="w-5 h-5 text-yellow-600" />
                                מדדי הצלחה (KPIs)
                              </h4>
                              <ul className="space-y-2">
                                {opening.kpis.map((kpi, index) => (
                                  <li key={index} className="p-2 bg-yellow-50 rounded-lg text-gray-700">
                                    {kpi}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                          
                          {/* יעדים מרכזיים - משימות נבחרות (ליום) */}
                          {opening.selectedTasks && opening.selectedTasks.length > 0 && (
                            <div>
                              <h4 className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                                <Target className="w-5 h-5 text-green-600" />
                                יעדים מרכזיים
                              </h4>
                              <div className="text-sm text-gray-600 mb-2">
                                {opening.selectedTasks.length} משימות נבחרו כיעדים מרכזיים
                              </div>
                            </div>
                          )}
                          
                          {/* חסמים */}
                          {opening.blockers && (
                            <div>
                              <h4 className="flex items-center gap-2 font-semibold text-gray-900 mb-2">
                                <XCircle className="w-5 h-5 text-red-600" />
                                חסמים צפויים
                              </h4>
                              <p className="text-gray-700 bg-red-50 p-3 rounded-lg">
                                {opening.blockers}
                              </p>
                            </div>
                          )}
                          
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            {/* פגישות */}
                            {opening.meetings && (
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-2">פגישות מתוכננות</h4>
                                <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                                  {opening.meetings}
                                </p>
                              </div>
                            )}
                            
                            {/* דדליינים */}
                            {opening.deadlines && (
                              <div>
                                <h4 className="font-semibold text-gray-900 mb-2">דדליינים חשובים</h4>
                                <p className="text-gray-700 bg-gray-50 p-3 rounded-lg">
                                  {opening.deadlines}
                                </p>
                              </div>
                            )}
                          </div>
                          {/* כפתור מחיקה */}
                        {(currentUser.role === 'org_admin' || currentUser.role === 'ceo') && (
                          <div className="mt-4 pt-4 border-t border-gray-100 flex justify-end">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteSummary(
                                  opening.id, 
                                  opening.periodType === 'day' 
                                    ? COLLECTIONS.DAY_OPENINGS 
                                    : COLLECTIONS.PERIOD_OPENINGS
                                );
                              }}
                              className="px-3 py-2 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-sm font-medium flex items-center gap-2 transition-colors"
                            >
                              <Trash2 className="w-4 h-4" />
                              מחק פתיחה
                            </button>
                          </div>
                        )}
                        </div>
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TeamSummaries;